import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertCircle,
  ArrowUpRight,
  Clock3,
  Check,
  Copy,
  ExternalLink,
  FileText,
  History,
  RefreshCw,
  RotateCcw,
  Search,
  Send,
  ShieldCheck,
  Sparkles,
} from "lucide-react";
import LoadingSpinner from "../../common/LoadingSpinner";
import { useProjectContext } from "../../../hooks/useProjectContext";
import { inspectionsAPI } from "../../../services/api";

const FOREMAN_ACTION_OPTIONS = [
  "Resolved - With Action",
  "Resolved - Without Action",
  "Pending",
];

const FILTERS = [
  { id: "active", label: "Open + Pending + Reopened" },
  { id: "open", label: "Open" },
  { id: "pending", label: "Pending" },
  { id: "reopened", label: "Reopened" },
  { id: "overdue", label: "Overdue" },
  { id: "responded", label: "Responded" },
  { id: "closed", label: "Closed" },
  { id: "all", label: "All" },
];

const norm = (value) => (value || "").toString().toLowerCase();
const value = (v) => (v === null || v === undefined || v === "" ? "-" : v);

const isPastDate = (raw) => {
  if (!raw) return false;
  const d = new Date(`${raw}T00:00:00`);
  if (Number.isNaN(d.getTime())) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return d < today;
};

const statusBadge = (status) => {
  const s = norm(status);
  if (s === "closed") return "border-gray-200 bg-gray-100 text-gray-700";
  if (s === "responded" || s === "answered") return "border-blue-100 bg-blue-50 text-blue-700";
  if (s === "reopened") return "border-orange-100 bg-orange-50 text-orange-700";
  if (s === "pending") return "border-amber-100 bg-amber-50 text-amber-700";
  return "border-emerald-100 bg-emerald-50 text-emerald-700";
};

const titleCase = (raw) =>
  value(raw)
    .toString()
    .replaceAll("_", " ")
    .split(" ")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

function KpiCard({ label, value: count, hint, icon: Icon, tone = "orange", onClick, active = false }) {
  const toneMap = {
    orange: {
      gradient: "from-orange-500 to-amber-500",
      icon: "bg-orange-50 text-orange-700",
      border: "border-orange-100",
      glow: "bg-orange-50",
      activeRing: "ring-orange-200",
    },
    amber: {
      gradient: "from-amber-500 to-yellow-500",
      icon: "bg-amber-50 text-amber-700",
      border: "border-amber-100",
      glow: "bg-amber-50",
      activeRing: "ring-amber-200",
    },
    blue: {
      gradient: "from-blue-500 to-sky-500",
      icon: "bg-blue-50 text-blue-700",
      border: "border-blue-100",
      glow: "bg-blue-50",
      activeRing: "ring-blue-200",
    },
    red: {
      gradient: "from-red-500 to-rose-500",
      icon: "bg-red-50 text-red-700",
      border: "border-red-100",
      glow: "bg-red-50",
      activeRing: "ring-red-200",
    },
  };
  const t = toneMap[tone] || toneMap.orange;
  const Component = onClick ? "button" : "div";
  return (
    <Component
      type={onClick ? "button" : undefined}
      onClick={onClick}
      className={`group relative min-w-0 overflow-hidden rounded-3xl border ${t.border} bg-white p-4 text-left shadow-sm transition hover:-translate-y-0.5 hover:shadow-md ${onClick ? "cursor-pointer focus:outline-none focus:ring-2 focus:ring-orange-400" : ""} ${active ? `ring-2 ${t.activeRing}` : ""}`}
    >
      <div className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${t.gradient}`} />
      <div className={`absolute -right-8 -top-8 h-24 w-24 rounded-full ${t.glow} opacity-70 transition group-hover:scale-110`} />
      <div className="relative flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="break-words text-xs font-bold uppercase tracking-[0.18em] text-gray-500">
            {label}
          </p>
          <p className="mt-2 text-3xl font-black tracking-tight text-gray-950">{count ?? 0}</p>
          <p className="mt-1 break-words text-xs text-gray-500">{hint}</p>
        </div>
        {Icon ? (
          <div className={`shrink-0 rounded-2xl ${t.icon} p-3 ring-1 ring-white/70`}>
            <Icon className="h-5 w-5" />
          </div>
        ) : null}
      </div>
    </Component>
  );
}


function CopyIssueNumberButton({ findingNumber, fallbackId, compact = false }) {
  const [copied, setCopied] = useState(false);
  const text = findingNumber || `Issue #${fallbackId}`;

  const copy = async (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = text;
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        document.execCommand("copy");
        textarea.remove();
      }
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1400);
    } catch (err) {
      console.error("Failed to copy issue number", err);
    }
  };

  return (
    <button
      type="button"
      onClick={copy}
      aria-label={`Copy ${text}`}
      title={copied ? "Copied" : "Copy issue number"}
      className={`inline-flex shrink-0 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-500 shadow-sm hover:border-orange-200 hover:bg-orange-50 hover:text-orange-700 ${compact ? "h-7 w-7" : "h-8 w-8"}`}
    >
      {copied ? <Check className={compact ? "h-3.5 w-3.5" : "h-4 w-4"} /> : <Copy className={compact ? "h-3.5 w-3.5" : "h-4 w-4"} />}
    </button>
  );
}

function ResponseModal({ finding, onClose, onSubmit, onOpenFile, loading }) {
  const [foremanAction, setForemanAction] = useState(finding?.foreman_action || "");
  const [responseNotes, setResponseNotes] = useState(finding?.response_notes || "");
  const [error, setError] = useState("");
  const [fileError, setFileError] = useState("");
  const [openingFileId, setOpeningFileId] = useState(null);

  useEffect(() => {
    setForemanAction(finding?.foreman_action || "");
    // For reopened issues, start with a blank note so the new response is added
    // as a new history entry instead of looking like the old comment is being edited.
    setResponseNotes(norm(finding?.status) === "reopened" ? "" : finding?.response_notes || "");
    setError("");
    setFileError("");
    setOpeningFileId(null);
  }, [finding]);

  if (!finding) return null;

  const openRelatedFile = async (file) => {
    if (!file?.id || !onOpenFile) return;

    setFileError("");
    setOpeningFileId(file.id);
    try {
      await onOpenFile(finding, file);
    } catch (err) {
      setFileError(
        err?.response?.data?.detail ||
          err?.message ||
          "The file could not be opened. Please try again.",
      );
    } finally {
      setOpeningFileId(null);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!foremanAction) {
      setError("Please select a response action.");
      return;
    }
    try {
      await onSubmit({
        foreman_action: foremanAction,
        response_notes: responseNotes.trim() || null,
      });
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to submit response.");
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4">
      <form onSubmit={submit} className="max-h-[calc(100vh-2rem)] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white shadow-xl">
        <div className="sticky top-0 border-b bg-white p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Assigned Issue</p>
              <div className="mt-1 flex min-w-0 items-center gap-2">
                <h3 className="min-w-0 break-words text-lg font-semibold text-gray-900">
                  {finding.finding_number || `Issue #${finding.id}`}
                </h3>
                <CopyIssueNumberButton
                  findingNumber={finding.finding_number}
                  fallbackId={finding.id}
                />
              </div>
            </div>
            <button type="button" onClick={onClose} className="rounded-lg border px-3 py-1.5 text-sm hover:bg-gray-50">
              Close
            </button>
          </div>
        </div>

        <div className="space-y-4 p-4">
          <div className="rounded-xl border bg-gray-50 p-3 text-sm text-gray-700">
            <div className="font-medium text-gray-900">Description</div>
            <div className="mt-1 break-words">{value(finding.description)}</div>
            <div className="mt-3 font-medium text-gray-900">Corrective Action</div>
            <div className="mt-1 break-words">{value(finding.corrective_action)}</div>
          </div>

          <div className="rounded-xl border border-gray-200 bg-white p-3">
            <div className="mb-2 flex items-center justify-between gap-3">
              <div className="flex min-w-0 items-center gap-2">
                <FileText className="h-4 w-4 shrink-0 text-orange-600" />
                <span className="text-sm font-medium text-gray-900">
                  Related Files
                </span>
              </div>
              <span className="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                {finding.files?.length || 0}
              </span>
            </div>

            {finding.files?.length ? (
              <div className="max-h-44 space-y-1.5 overflow-y-auto pr-1">
                {finding.files.map((file) => {
                  const opening = Number(openingFileId) === Number(file.id);
                  return (
                    <button
                      key={`${file.kind || "file"}-${file.id}`}
                      type="button"
                      disabled={opening || !file.id}
                      onClick={() => openRelatedFile(file)}
                      className="flex w-full min-w-0 items-center justify-between gap-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-left text-sm transition hover:border-orange-200 hover:bg-orange-50 disabled:cursor-wait disabled:opacity-60"
                      title={`Open ${file.name || "file"} in a new browser tab`}
                    >
                      <span className="flex min-w-0 items-center gap-2">
                        <FileText className="h-4 w-4 shrink-0 text-gray-500" />
                        <span className="min-w-0">
                          <span className="block truncate font-medium text-gray-800">
                            {file.name || `File #${file.id}`}
                          </span>
                          <span className="block truncate text-xs text-gray-500">
                            {file.document_type || file.category || "Inspection file"}
                          </span>
                        </span>
                      </span>
                      <span className="inline-flex shrink-0 items-center gap-1 text-xs font-semibold text-orange-700">
                        {opening ? (
                          <>
                            <RefreshCw className="h-3.5 w-3.5 animate-spin" /> Opening
                          </>
                        ) : (
                          <>
                            Open <ExternalLink className="h-3.5 w-3.5" />
                          </>
                        )}
                      </span>
                    </button>
                  );
                })}
              </div>
            ) : (
              <p className="text-xs text-gray-500">
                No files are attached to this inspection.
              </p>
            )}

            {fileError ? (
              <div className="mt-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                {fileError}
              </div>
            ) : null}
          </div>

          <label className="block text-sm">
            <span className="mb-1 block font-medium text-gray-700">Foreman Action *</span>
            <select
              required
              value={foremanAction}
              onChange={(e) => setForemanAction(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
            >
              <option value="">Select action</option>
              {FOREMAN_ACTION_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </label>

          <label className="block text-sm">
            <span className="mb-1 block font-medium text-gray-700">Response Notes / New Comment</span>
            <textarea
              rows={4}
              value={responseNotes}
              onChange={(e) => setResponseNotes(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
              placeholder="Add the new update. Previous notes remain saved in View History."
            />
          </label>

          <div className="rounded-lg border border-blue-100 bg-blue-50 p-3 text-xs text-blue-700">
            Submitting saves this as a new history entry. Previous comments are not deleted.
          </div>

          {error ? <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div> : null}
        </div>

        <div className="flex flex-col-reverse gap-2 border-t p-4 sm:flex-row sm:justify-end">
          <button type="button" onClick={onClose} className="rounded-lg border px-4 py-2 text-sm hover:bg-gray-50">
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-orange-500 px-4 py-2 text-sm font-semibold text-white hover:bg-orange-600 disabled:opacity-60"
          >
            {loading ? <LoadingSpinner size="small" /> : <Send className="h-4 w-4" />}
            Submit Response
          </button>
        </div>
      </form>
    </div>
  );
}

function HistoryModal({ finding, comments, loading, onClose }) {
  if (!finding) return null;
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4">
      <div className="max-h-[calc(100vh-2rem)] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white shadow-xl">
        <div className="sticky top-0 flex items-start justify-between gap-3 border-b bg-white p-4">
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Issue History</p>
            <h3 className="break-words text-lg font-semibold text-gray-900">
              {finding.finding_number || `Issue #${finding.id}`}
            </h3>
          </div>
          <button type="button" onClick={onClose} className="rounded-lg border px-3 py-1.5 text-sm hover:bg-gray-50">
            Close
          </button>
        </div>
        <div className="space-y-3 p-4">
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-gray-500"><LoadingSpinner size="small" /> Loading history...</div>
          ) : comments.length === 0 ? (
            <div className="rounded-xl border border-dashed p-4 text-sm text-gray-500">No history comments yet.</div>
          ) : (
            comments.map((c) => (
              <div key={c.id} className="rounded-xl border bg-white p-3 text-sm">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="font-medium text-gray-900">{c.user_name || `User #${c.user_id}`}</div>
                  <div className="text-xs text-gray-500">{c.created_at ? new Date(c.created_at).toLocaleString() : "-"}</div>
                </div>
                <div className="mt-1 flex flex-wrap gap-2 text-xs text-gray-500">
                  <span>{titleCase(c.comment_type)}</span>
                  {c.from_status || c.to_status ? <span>{titleCase(c.from_status)} → {titleCase(c.to_status)}</span> : null}
                  {c.user_role ? <span>{titleCase(c.user_role)}</span> : null}
                </div>
                <div className="mt-2 whitespace-pre-wrap break-words text-gray-700">{c.body}</div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}


function IssueContextModal({ finding, comments, loading, onClose, onOpenFile }) {
  const [fileError, setFileError] = useState("");
  const [openingFileId, setOpeningFileId] = useState(null);

  useEffect(() => {
    setFileError("");
    setOpeningFileId(null);
  }, [finding?.id]);

  if (!finding) return null;

  const Field = ({ label, children }) => (
    <div className="min-w-0">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
        {label}
      </div>
      <div className="mt-1 break-words text-sm text-gray-900">{children || "-"}</div>
    </div>
  );

  const openFile = async (file) => {
    if (!file?.id || !onOpenFile) return;
    setFileError("");
    setOpeningFileId(file.id);
    try {
      await onOpenFile(finding, file);
    } catch (err) {
      setFileError(
        err?.response?.data?.detail ||
          err?.message ||
          "The file could not be opened. Please try again.",
      );
    } finally {
      setOpeningFileId(null);
    }
  };

  return (
    <div className="fixed inset-0 z-[75] flex items-center justify-center bg-black/40 p-4">
      <div className="flex max-h-[calc(100vh-2rem)] w-full max-w-4xl flex-col overflow-hidden rounded-2xl bg-white shadow-xl">
        <div className="flex items-start justify-between gap-3 border-b bg-white p-4">
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Issue Context</p>
            <h3 className="mt-1 break-words text-lg font-semibold text-gray-900">
              {finding.finding_number || `Issue #${finding.id}`}
            </h3>
            <p className="mt-1 break-words text-sm text-gray-500">
              Inspection {value(finding.inspection_number)} • {value(finding.inspection_type)}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-lg border px-3 py-1.5 text-sm hover:bg-gray-50"
          >
            Close
          </button>
        </div>

        <div className="space-y-4 overflow-y-auto p-4">
          <div className="grid grid-cols-1 gap-3 rounded-xl border bg-gray-50 p-3 sm:grid-cols-2 lg:grid-cols-4">
            <Field label="Inspection No.">{value(finding.inspection_number)}</Field>
            <Field label="Inspection Date">{value(finding.inspection_date)}</Field>
            <Field label="Inspection Type">{value(finding.inspection_type)}</Field>
            <Field label="Site Location">{value(finding.location)}</Field>
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div className="rounded-xl border p-4">
              <h4 className="mb-3 font-semibold text-gray-900">Issue details</h4>
              <div className="space-y-3">
                <Field label="Description">{value(finding.description)}</Field>
                <Field label="Requested Corrective Action">{value(finding.corrective_action)}</Field>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <Field label="Severity">{titleCase(finding.severity)}</Field>
                  <Field label="Due Date">{value(finding.due_date)}</Field>
                  <Field label="Status">{titleCase(finding.status || "open")}</Field>
                  <Field label="Created At">
                    {finding.created_at ? new Date(finding.created_at).toLocaleString() : "-"}
                  </Field>
                </div>
              </div>
            </div>

            <div className="rounded-xl border p-4">
              <h4 className="mb-3 font-semibold text-gray-900">Assignment and response</h4>
              <div className="space-y-3">
                <Field label="Responsible Person">
                  {finding.responsible_user_name || finding.responsible_user_email || "-"}
                </Field>
                <Field label="Response Action">{finding.foreman_action || "No response submitted yet."}</Field>
                <Field label="Response Notes">{finding.response_notes || finding.response_text || "-"}</Field>
                <Field label="Responded At">
                  {finding.responded_at ? new Date(finding.responded_at).toLocaleString() : "-"}
                </Field>
              </div>
            </div>
          </div>

          <div className="rounded-xl border p-4">
            <div className="mb-3 flex items-center justify-between gap-3">
              <h4 className="flex items-center gap-2 font-semibold text-gray-900">
                <FileText className="h-4 w-4 text-orange-500" /> Related Files
              </h4>
              <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                {finding.files?.length || 0}
              </span>
            </div>

            {finding.files?.length ? (
              <div className="max-h-48 space-y-1.5 overflow-y-auto pr-1">
                {finding.files.map((file) => {
                  const opening = Number(openingFileId) === Number(file.id);
                  return (
                    <button
                      key={`${file.kind || "file"}-${file.id}`}
                      type="button"
                      disabled={opening || !file.id}
                      onClick={() => openFile(file)}
                      className="flex w-full min-w-0 items-center justify-between gap-3 rounded-lg border bg-gray-50 px-3 py-2 text-left text-sm hover:border-orange-200 hover:bg-orange-50 disabled:cursor-wait disabled:opacity-60"
                    >
                      <span className="min-w-0 truncate font-medium text-gray-800">
                        {file.name || `File #${file.id}`}
                      </span>
                      <span className="inline-flex shrink-0 items-center gap-1 text-xs font-semibold text-orange-700">
                        {opening ? (
                          <>
                            <RefreshCw className="h-3.5 w-3.5 animate-spin" /> Opening
                          </>
                        ) : (
                          <>
                            Open <ExternalLink className="h-3.5 w-3.5" />
                          </>
                        )}
                      </span>
                    </button>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-gray-500">No files are attached to this inspection.</p>
            )}

            {fileError ? (
              <div className="mt-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                {fileError}
              </div>
            ) : null}
          </div>

          <div className="rounded-xl border p-4">
            <h4 className="mb-3 flex items-center gap-2 font-semibold text-gray-900">
              <History className="h-4 w-4 text-orange-500" /> Issue history
            </h4>
            {loading ? (
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <LoadingSpinner size="small" /> Loading history...
              </div>
            ) : comments.length === 0 ? (
              <div className="rounded-xl border border-dashed p-4 text-sm text-gray-500">
                No history is available.
              </div>
            ) : (
              <div className="max-h-64 space-y-2 overflow-y-auto">
                {comments.map((c) => (
                  <div key={c.id} className="rounded-lg border bg-gray-50 p-3 text-sm">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="font-medium text-gray-900">
                        {c.user_name || `User #${c.user_id}`}
                        {c.user_role ? ` • ${titleCase(c.user_role)}` : ""}
                      </div>
                      <div className="text-xs text-gray-500">
                        {c.created_at ? new Date(c.created_at).toLocaleString() : "-"}
                      </div>
                    </div>
                    <div className="mt-1 flex flex-wrap gap-2 text-xs text-gray-500">
                      <span>{titleCase(c.comment_type)}</span>
                      {c.from_status || c.to_status ? (
                        <span>{titleCase(c.from_status)} → {titleCase(c.to_status)}</span>
                      ) : null}
                    </div>
                    <div className="mt-2 whitespace-pre-wrap break-words text-gray-700">{c.body}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function ReopenModal({ finding, onClose, onSubmit, loading }) {
  const [comment, setComment] = useState("");
  const [error, setError] = useState("");
  if (!finding) return null;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!comment.trim()) {
      setError("Reopen comment is required.");
      return;
    }
    try {
      await onSubmit({ comment: comment.trim() });
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to reopen issue.");
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4">
      <form onSubmit={submit} className="w-full max-w-lg rounded-2xl bg-white shadow-xl">
        <div className="border-b p-4">
          <h3 className="text-lg font-semibold text-gray-900">Reopen Issue</h3>
          <p className="break-words text-sm text-gray-500">{finding.finding_number || `Issue #${finding.id}`}</p>
        </div>
        <div className="space-y-3 p-4">
          <label className="block text-sm">
            <span className="mb-1 block font-medium text-gray-700">Required Comment</span>
            <textarea
              required
              rows={4}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
              placeholder="Why is this issue being reopened?"
            />
          </label>
          <div className="rounded-lg border border-blue-100 bg-blue-50 p-3 text-xs text-blue-700">
            Submitting saves this as a new history entry. Previous comments are not deleted.
          </div>

          {error ? <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div> : null}
        </div>
        <div className="flex flex-col-reverse gap-2 border-t p-4 sm:flex-row sm:justify-end">
          <button type="button" onClick={onClose} className="rounded-lg border px-4 py-2 text-sm hover:bg-gray-50">Cancel</button>
          <button type="submit" disabled={loading} className="inline-flex items-center justify-center gap-2 rounded-lg bg-orange-500 px-4 py-2 text-sm font-semibold text-white hover:bg-orange-600 disabled:opacity-60">
            {loading ? <LoadingSpinner size="small" /> : <RotateCcw className="h-4 w-4" />}
            Reopen
          </button>
        </div>
      </form>
    </div>
  );
}

function EscalationModal({ finding, targets, loading, saving, onClose, onSubmit }) {
  const [targetUserId, setTargetUserId] = useState("");
  const [reason, setReason] = useState("");

  useEffect(() => {
    setTargetUserId("");
    setReason("");
  }, [finding]);

  if (!finding) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-xl rounded-2xl bg-white shadow-xl">
        <div className="border-b p-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Escalate Issue</div>
          <h3 className="mt-1 text-lg font-semibold text-gray-900">
            {finding.finding_number || `Issue #${finding.id}`}
          </h3>
        </div>
        <div className="space-y-4 p-4">
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-gray-500"><LoadingSpinner size="small" /> Loading reporting-path targets...</div>
          ) : targets.length === 0 ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
              No valid escalation target is available in the published organization chart.
            </div>
          ) : (
            <>
              <label className="block text-sm">
                <span className="mb-1 block font-medium text-gray-700">Escalate To *</span>
                <select
                  value={targetUserId}
                  onChange={(e) => setTargetUserId(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
                >
                  <option value="">Select reporting-path target</option>
                  {targets.map((target) => (
                    <option key={target.user_id} value={target.user_id}>
                      {target.name} — {titleCase(target.role)}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block text-sm">
                <span className="mb-1 block font-medium text-gray-700">Reason *</span>
                <textarea
                  rows={4}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Describe why this issue needs escalation or technical guidance..."
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </label>
            </>
          )}
        </div>
        <div className="flex justify-end gap-2 border-t p-4">
          <button type="button" onClick={onClose} className="rounded-lg border px-4 py-2 text-sm hover:bg-gray-50">Cancel</button>
          <button
            type="button"
            disabled={saving || loading || !targetUserId || !reason.trim()}
            onClick={() => onSubmit({ escalated_to_user_id: Number(targetUserId), reason: reason.trim() })}
            className="inline-flex items-center gap-2 rounded-lg bg-gray-900 px-4 py-2 text-sm font-semibold text-white hover:bg-black disabled:opacity-60"
          >
            {saving ? <LoadingSpinner size="small" /> : <ArrowUpRight className="h-4 w-4" />} Escalate
          </button>
        </div>
      </div>
    </div>
  );
}

function IssueCard({ issue, onRespond, onHistory, onReopen, onViewContext, onEscalate, compact }) {
  const status = norm(issue.status);
  const closed = status === "closed";
  const overdue = isPastDate(issue.due_date) && !closed;
  const canRespond = !closed;

  const handleCardClick = (e) => {
    if (e.target.closest("button")) return;
    if (canRespond) onRespond(issue);
    else onHistory(issue);
  };

  if (compact) {
    return (
      <div
        role="button"
        tabIndex={0}
        onClick={handleCardClick}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") handleCardClick(e);
        }}
        className="group min-w-0 cursor-pointer rounded-2xl border border-gray-200 bg-white p-3 shadow-sm transition hover:border-orange-200 hover:bg-orange-50/20 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-orange-400"
      >
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-[minmax(0,1.1fr)_130px_120px_minmax(260px,auto)] lg:items-center">
          <div className="min-w-0">
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              <span className="break-words text-sm font-bold text-gray-950">
                {issue.finding_number || `Issue #${issue.id}`}
              </span>
              <CopyIssueNumberButton
                findingNumber={issue.finding_number}
                fallbackId={issue.id}
                compact
              />
              <span className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${statusBadge(issue.status)}`}>
                {titleCase(issue.status || "open")}
              </span>
              {overdue ? <span className="rounded-full border border-red-100 bg-red-50 px-2 py-0.5 text-[11px] font-semibold text-red-700">Overdue</span> : null}
            </div>
            <div className="mt-1 line-clamp-2 break-words text-sm text-gray-700">
              {value(issue.description)}
            </div>
            <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-gray-500">
              <span>Inspection No.: {value(issue.inspection_number)}</span>
              <span>Site location: {value(issue.location)}</span>
              <span>Severity: {value(issue.severity)}</span>
            </div>
          </div>

          <div className="text-xs text-gray-600">
            <span className="font-semibold text-gray-800">Due:</span> {value(issue.due_date)}
          </div>
          <div className="text-xs text-gray-600">
            <span className="font-semibold text-gray-800">Type:</span> {value(issue.inspection_type)}
          </div>

          <div className="flex min-w-0 flex-wrap gap-2 lg:justify-end">
            {canRespond ? (
              <button type="button" onClick={() => onRespond(issue)} className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-orange-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-orange-600">
                <Send className="h-3.5 w-3.5" />
                {issue.foreman_action || status === "pending" ? "Update" : "Respond"}
              </button>
            ) : null}
            {canRespond && onEscalate ? (
              <button type="button" onClick={() => onEscalate(issue)} className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-xs hover:bg-white">
                <ArrowUpRight className="h-3.5 w-3.5" /> Escalate
              </button>
            ) : null}
            <button type="button" onClick={() => onHistory(issue)} className="inline-flex items-center justify-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs hover:bg-white">
              <History className="h-3.5 w-3.5" /> History
            </button>
            <button type="button" onClick={() => onViewContext(issue)} className="inline-flex items-center justify-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs hover:bg-white">
              <ExternalLink className="h-3.5 w-3.5" /> Context
            </button>
            {closed ? (
              <button type="button" onClick={() => onReopen(issue)} className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-orange-200 px-3 py-1.5 text-xs text-orange-700 hover:bg-orange-50">
                <RotateCcw className="h-3.5 w-3.5" /> Reopen
              </button>
            ) : null}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleCardClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") handleCardClick(e);
      }}
      className="min-w-0 cursor-pointer rounded-3xl border border-gray-200 bg-white p-4 shadow-sm transition hover:-translate-y-0.5 hover:border-orange-200 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-orange-400"
    >
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="break-words text-base font-semibold text-gray-900">
              {issue.finding_number || `Issue #${issue.id}`}
            </h3>
            <CopyIssueNumberButton
              findingNumber={issue.finding_number}
              fallbackId={issue.id}
            />
            <span className={`rounded-full border px-2 py-0.5 text-xs font-medium ${statusBadge(issue.status)}`}>
              {titleCase(issue.status || "open")}
            </span>
            {overdue ? <span className="rounded-full border border-red-100 bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700">Overdue</span> : null}
          </div>

          <div className="mt-2 grid grid-cols-1 gap-2 text-xs text-gray-500 sm:grid-cols-2 lg:grid-cols-4">
            <div className="min-w-0 break-words"><span className="font-medium text-gray-700">Inspection No.:</span> {value(issue.inspection_number)}</div>
            <div className="min-w-0 break-words"><span className="font-medium text-gray-700">Date:</span> {value(issue.inspection_date)}</div>
            <div className="min-w-0 break-words"><span className="font-medium text-gray-700">Type:</span> {value(issue.inspection_type)}</div>
            <div className="min-w-0 break-words"><span className="font-medium text-gray-700">Due:</span> {value(issue.due_date)}</div>
          </div>

          <div className="mt-3 grid grid-cols-1 gap-3 text-sm md:grid-cols-2">
            <div className="min-w-0">
              <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Description</div>
              <div className="mt-1 break-words text-gray-800">{value(issue.description)}</div>
            </div>
            <div className="min-w-0">
              <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Corrective Action</div>
              <div className="mt-1 break-words text-gray-800">{value(issue.corrective_action)}</div>
            </div>
          </div>
          <div className="mt-3 grid grid-cols-1 gap-2 text-xs text-gray-500 sm:grid-cols-2">
            <div className="min-w-0 break-words"><span className="font-medium text-gray-700">Site location:</span> {value(issue.location)}</div>
            <div className="min-w-0 break-words"><span className="font-medium text-gray-700">Severity:</span> {value(issue.severity)}</div>
          </div>
        </div>

        <div className="flex shrink-0 flex-wrap gap-2 lg:justify-end">
          {canRespond ? (
            <button type="button" onClick={() => onRespond(issue)} className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-orange-500 px-3 py-2 text-sm font-semibold text-white hover:bg-orange-600 sm:flex-none">
              <Send className="h-4 w-4" />
              {issue.foreman_action || status === "pending" ? "Update Response" : "Respond"}
            </button>
          ) : null}
          {canRespond && onEscalate ? (
            <button type="button" onClick={() => onEscalate(issue)} className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg border px-3 py-2 text-sm hover:bg-gray-50 sm:flex-none">
              <ArrowUpRight className="h-4 w-4" /> Escalate
            </button>
          ) : null}
          <button type="button" onClick={() => onHistory(issue)} className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg border px-3 py-2 text-sm hover:bg-gray-50 sm:flex-none">
            <History className="h-4 w-4" /> View History
          </button>
          <button type="button" onClick={() => onViewContext(issue)} className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg border px-3 py-2 text-sm hover:bg-gray-50 sm:flex-none">
            <ExternalLink className="h-4 w-4" /> View Context
          </button>
          {closed ? (
            <button type="button" onClick={() => onReopen(issue)} className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-orange-200 px-3 py-2 text-sm text-orange-700 hover:bg-orange-50 sm:flex-none">
              <RotateCcw className="h-4 w-4" /> Reopen Issue
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

const MyIssuesView = ({ dashboardMode = false, onNavigate }) => {
  const { activeProject } = useProjectContext();
  const projectId = activeProject?.id;

  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState(dashboardMode ? "active" : "active");
  const [query, setQuery] = useState("");
  const [responseIssue, setResponseIssue] = useState(null);
  const [escalationIssue, setEscalationIssue] = useState(null);
  const [escalationTargets, setEscalationTargets] = useState([]);
  const [escalationLoading, setEscalationLoading] = useState(false);
  const [escalationSaving, setEscalationSaving] = useState(false);
  const [historyIssue, setHistoryIssue] = useState(null);
  const [contextIssue, setContextIssue] = useState(null);
  const [contextComments, setContextComments] = useState([]);
  const [contextLoading, setContextLoading] = useState(false);
  const [reopenIssue, setReopenIssue] = useState(null);
  const [comments, setComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [pendingFindingId, setPendingFindingId] = useState(null);

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError("");
    try {
      const res = await inspectionsAPI.listMyAssignedFindings(projectId);
      setIssues(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to load assigned issues.");
      setIssues([]);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    load();
  }, [load]);

  const counts = useMemo(() => {
    const open = issues.filter((i) => norm(i.status) === "open" || !i.status).length;
    const pending = issues.filter((i) => norm(i.status) === "pending").length;
    const reopened = issues.filter((i) => norm(i.status) === "reopened").length;
    const overdue = issues.filter((i) => isPastDate(i.due_date) && norm(i.status) !== "closed").length;
    return { open, pending, reopened, overdue };
  }, [issues]);

  const filteredIssues = useMemo(() => {
    const q = query.trim().toLowerCase();
    return issues
      .filter((issue) => {
        const s = norm(issue.status || "open");
        const overdue = isPastDate(issue.due_date) && s !== "closed";
        if (filter === "active") return ["open", "pending", "reopened", ""].includes(s);
        if (filter === "overdue") return overdue;
        if (filter === "all") return true;
        return s === filter;
      })
      .filter((issue) => {
        if (!q) return true;
        return [
          issue.finding_number,
          issue.inspection_number,
          issue.location,
          issue.description,
          issue.status,
          issue.corrective_action,
          issue.inspection_type,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(q);
      })
      .sort((a, b) => {
        const aClosed = norm(a.status) === "closed" ? 1 : 0;
        const bClosed = norm(b.status) === "closed" ? 1 : 0;
        if (aClosed !== bClosed) return aClosed - bClosed;
        return String(a.due_date || "9999-12-31").localeCompare(String(b.due_date || "9999-12-31"));
      });
  }, [issues, filter, query]);

  const openHistory = async (issue) => {
    setHistoryIssue(issue);
    setComments([]);
    setCommentsLoading(true);
    try {
      const res = await inspectionsAPI.listFindingComments(projectId, issue.inspection_id, issue.id);
      setComments(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setComments([]);
    } finally {
      setCommentsLoading(false);
    }
  };


  const openIssueFromNotification = (issue) => {
    if (!issue) return;
    setFilter("all");
    setQuery("");
    const status = norm(issue.status);
    if (["closed", "responded", "answered"].includes(status)) {
      openHistory(issue);
    } else {
      setResponseIssue(issue);
    }
  };

  useEffect(() => {
    const handler = (event) => {
      const detail = event?.detail || {};
      if (detail.action !== "openFinding" && !detail.findingId) return;
      if (detail.projectId && projectId && Number(detail.projectId) !== Number(projectId)) return;
      if (!detail.findingId) return;
      setFilter("all");
      setQuery("");
      setPendingFindingId(Number(detail.findingId));
    };
    window.addEventListener("smokyq:notification-open", handler);
    return () => window.removeEventListener("smokyq:notification-open", handler);
  }, [projectId]);

  useEffect(() => {
    if (!pendingFindingId || loading) return;
    const match = issues.find((issue) => Number(issue.id) === Number(pendingFindingId));
    if (!match) return;
    setPendingFindingId(null);
    openIssueFromNotification(match);
  }, [pendingFindingId, issues, loading]);

  const openRelatedFile = useCallback(
    async (_finding, file) => {
      if (!projectId || !file?.id) {
        throw new Error("File information is incomplete.");
      }

      const previewWindow = window.open("", "_blank");
      if (previewWindow) {
        previewWindow.opener = null;
        previewWindow.document.title = file.name || "Opening file";
        previewWindow.document.body.innerHTML =
          '<div style="font-family:Arial,sans-serif;padding:24px;color:#374151">Opening file…</div>';
      }

      try {
        const res = await inspectionsAPI.getDocumentDownloadUrl(projectId, file.id);
        const freshUrl = res?.data?.url;
        if (!freshUrl) throw new Error("A fresh file link could not be generated.");

        if (previewWindow && !previewWindow.closed) {
          previewWindow.location.replace(freshUrl);
        } else {
          window.location.assign(freshUrl);
        }
      } catch (err) {
        if (previewWindow && !previewWindow.closed) previewWindow.close();
        throw err;
      }
    },
    [projectId],
  );

  const openEscalation = async (issue) => {
    setEscalationIssue(issue);
    setEscalationTargets([]);
    setEscalationLoading(true);
    setError("");
    try {
      const res = await inspectionsAPI.getFindingEscalationTargets(projectId, issue.id);
      setEscalationTargets(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to load escalation targets.");
    } finally {
      setEscalationLoading(false);
    }
  };

  const submitEscalation = async (payload) => {
    if (!escalationIssue) return;
    setEscalationSaving(true);
    setError("");
    try {
      await inspectionsAPI.createFindingEscalation(projectId, escalationIssue.id, payload);
      setEscalationIssue(null);
      setEscalationTargets([]);
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to escalate issue.");
    } finally {
      setEscalationSaving(false);
    }
  };

  const submitResponse = async (payload) => {
    if (!responseIssue) return;
    setActionLoading(true);
    try {
      await inspectionsAPI.submitFindingResponse(projectId, responseIssue.inspection_id, responseIssue.id, payload);
      setResponseIssue(null);
      await load();
    } finally {
      setActionLoading(false);
    }
  };

  const submitReopen = async (payload) => {
    if (!reopenIssue) return;
    setActionLoading(true);
    try {
      await inspectionsAPI.reopenFinding(projectId, reopenIssue.inspection_id, reopenIssue.id, payload);
      setReopenIssue(null);
      await load();
    } finally {
      setActionLoading(false);
    }
  };

  const viewContext = async (issue) => {
    if (!issue) return;
    setContextIssue(issue);
    setContextComments([]);
    if (!projectId || !issue.inspection_id || !issue.id) return;

    setContextLoading(true);
    try {
      const res = await inspectionsAPI.listFindingComments(
        projectId,
        issue.inspection_id,
        issue.id,
      );
      setContextComments(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setContextComments([]);
      setError(err?.response?.data?.detail || "Failed to load issue context history.");
    } finally {
      setContextLoading(false);
    }
  };

  const dashboardItems = dashboardMode ? filteredIssues.slice(0, 8) : filteredIssues;

  return (
    <div className="min-w-0 space-y-5">
      <div className={`relative overflow-hidden rounded-3xl border ${dashboardMode ? "border-orange-100 bg-gradient-to-br from-orange-50 via-white to-slate-50 p-5 shadow-sm" : "border-transparent bg-transparent"}`}>
        {dashboardMode ? (
          <>
            <div className="absolute -right-12 -top-12 h-36 w-36 rounded-full bg-orange-200/40 blur-2xl" />
            <div className="absolute -bottom-16 left-1/3 h-32 w-32 rounded-full bg-blue-200/30 blur-2xl" />
          </>
        ) : null}
        <div className="relative flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="min-w-0">
            <div className="inline-flex items-center gap-2 rounded-full border border-orange-100 bg-white/80 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-orange-700 shadow-sm">
              <Sparkles className="h-3.5 w-3.5" /> Issue command center
            </div>
            <h2 className="mt-3 break-words text-2xl font-black tracking-tight text-gray-950">
              {dashboardMode ? "My Assigned Issues" : "My Issues"}
            </h2>
            <p className="mt-1 max-w-3xl break-words text-sm text-gray-600">
              Open the issue directly, submit a response, update pending items, and review the conversation history from one compact issue queue.
            </p>
          </div>
          <button type="button" onClick={load} disabled={loading} className="inline-flex items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white/90 px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-60">
            {loading ? <LoadingSpinner size="small" /> : <RefreshCw className="h-4 w-4" />}
            Refresh
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard label="Open Issues" value={counts.open} hint="Require initial response" icon={AlertCircle} tone="orange" active={filter === "open"} onClick={() => setFilter("open")} />
        <KpiCard label="Pending Issues" value={counts.pending} hint="Temporary blocker recorded" icon={Clock3} tone="amber" active={filter === "pending"} onClick={() => setFilter("pending")} />
        <KpiCard label="Reopened Issues" value={counts.reopened} hint="Closed issues reopened" icon={RotateCcw} tone="blue" active={filter === "reopened"} onClick={() => setFilter("reopened")} />
        <KpiCard label="Overdue Issues" value={counts.overdue} hint="Due date has passed" icon={ShieldCheck} tone="red" active={filter === "overdue"} onClick={() => setFilter("overdue")} />
      </div>

      {!dashboardMode ? (
        <div className="rounded-2xl border border-gray-200 bg-white p-3 shadow-sm">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="relative min-w-0 flex-1">
              <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search issue number, inspection number, site location, description, status..."
                className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>
            <div className="flex min-w-0 flex-wrap gap-2">
              {FILTERS.map((f) => (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => setFilter(f.id)}
                  className={`rounded-full border px-3 py-1.5 text-xs font-medium ${filter === f.id ? "border-orange-500 bg-orange-500 text-white" : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50"}`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      ) : null}

      {error ? <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}</div> : null}

      {loading ? (
        <div className="flex items-center gap-2 rounded-xl border border-dashed p-5 text-sm text-gray-500">
          <LoadingSpinner size="small" /> Loading assigned issues...
        </div>
      ) : dashboardItems.length === 0 ? (
        <div className="rounded-xl border border-dashed p-5 text-sm text-gray-500">
          No assigned issues require your action.
        </div>
      ) : (
        <div className="space-y-3">
          {dashboardItems.map((issue) => (
            <IssueCard
              key={issue.id}
              issue={issue}
              compact={true}
              onRespond={setResponseIssue}
              onHistory={openHistory}
              onReopen={setReopenIssue}
              onViewContext={viewContext}
              onEscalate={openEscalation}
            />
          ))}
        </div>
      )}

      {dashboardMode && filteredIssues.length > dashboardItems.length ? (
        <button type="button" onClick={() => onNavigate?.("my-issues")} className="rounded-lg border px-4 py-2 text-sm font-medium hover:bg-gray-50">
          View all assigned issues
        </button>
      ) : null}

      <EscalationModal
        finding={escalationIssue}
        targets={escalationTargets}
        loading={escalationLoading}
        saving={escalationSaving}
        onClose={() => setEscalationIssue(null)}
        onSubmit={submitEscalation}
      />
      <ResponseModal
        finding={responseIssue}
        onClose={() => setResponseIssue(null)}
        onSubmit={submitResponse}
        onOpenFile={openRelatedFile}
        loading={actionLoading}
      />
      <IssueContextModal
        finding={contextIssue}
        comments={contextComments}
        loading={contextLoading}
        onClose={() => setContextIssue(null)}
        onOpenFile={openRelatedFile}
      />
      <HistoryModal finding={historyIssue} comments={comments} loading={commentsLoading} onClose={() => setHistoryIssue(null)} />
      <ReopenModal finding={reopenIssue} onClose={() => setReopenIssue(null)} onSubmit={submitReopen} loading={actionLoading} />
    </div>
  );
};

export default MyIssuesView;
